/**
 * Devolved-module map, keyed by the same labelKey vocabulary as
 * tenant.hiddenSections so the sidebar filter gains one condition rather than a
 * parallel mechanism.
 *
 * Only genuinely devolved modules appear here. Anything absent is UK-wide - the
 * API enforces the same rule via middleware/nationRegistry, and these two lists
 * must agree: this one decides what is SHOWN, that one decides what is SERVED.
 */
import type { Nation } from './branchesApi';

export const MODULE_NATIONS: Readonly<Record<string, readonly Nation[]>> = {
  // Discharge Medicines Review is an NHS Wales service.
  welsh_dmr: ['WLS'],
  // `fp34c` WAS here as England-only, on the footing that Wales claims
  // elsewhere. Since 2026-10-04 (TODO 1606) a Welsh branch has the same monthly
  // claim check, priced from Wales's own fees, so it is UK-wide and unlisted.
  // What differs is its NAME - see MODULE_LABELS below.
  // Pharmacy First, its triage front end, and the contraception service are
  // NHS England commissioned services.
  pharmacy_first: ['ENG'],
  pf_triage: ['ENG'],
  pcs: ['ENG'],
  // The New Medicine Service and Hypertension Case-Finding are England CPCF
  // advanced services. The opportunities engine already tags both 'ENG' in its
  // FEE_SOURCE, so leaving them off here made the modules and the engine
  // disagree: a Welsh branch was invited to record cases it cannot claim for.
  nms: ['ENG'],
  hypertension: ['ENG'],
  // Independent Prescribing (PIPS) is the Welsh service - Choose Pharmacy's
  // Independent Prescribing module, covering common ailments and contraception.
  // The tariff carries it under Wales only.
  pips: ['WLS'],
};

/** Whether a module is visible in a nation. Unlisted modules are UK-wide. */
export function moduleVisibleInNation(labelKey: string, nation: Nation): boolean {
  const nations = MODULE_NATIONS[labelKey];
  return !nations || nations.includes(nation);
}

/**
 * A module both nations have, that goes by a different name in one of them.
 *
 * The other half of the map above. That one answers "does this nation have
 * the module"; this answers "what is it called there". FP34C is the name of
 * England's form, and a Welsh branch shown a tile reading "FP34C Submissions"
 * is being told it files England's paperwork.
 *
 * Only the label moves. `labelKey` stays `fp34c` everywhere it is an identity
 * - the route, the test id, tenant.hiddenSections - because those must not
 * change with the branch somebody happens to be standing in.
 */
const MODULE_LABELS: Readonly<Record<string, Partial<Record<Nation, string>>>> = {
  fp34c: { WLS: 'fp34c_wls' },
};

/** The `sidebar.nav` key holding this module's name in this nation. */
export function moduleLabelKey(labelKey: string, nation: Nation): string {
  return MODULE_LABELS[labelKey]?.[nation] ?? labelKey;
}
